import PageNavbar from "@/components/feature/PageNavbar";
import PageFooter from "@/components/feature/PageFooter";

const PRODUCTS = [
  { name: "CleanMaster Pro", category: "Cleaner", desc: "Deep system cleaning and junk removal for peak device performance." },
  { name: "AI Optimizer", category: "AI Utility", desc: "Smart resource management powered by on-device machine learning." },
  { name: "BoostMax", category: "Optimizer", desc: "One-tap RAM cleanup and background process killer." },
  { name: "DiskSweeper", category: "Cleaner", desc: "Visual storage analyzer that finds hidden space hogs instantly." },
  { name: "AppLock Shield", category: "Security", desc: "Biometric app protection with intrusion detection alerts." },
  { name: "NetGuard", category: "Security", desc: "Real-time network monitoring and firewall for mobile." },
  { name: "FileRescue", category: "Recovery", desc: "Recover deleted photos, videos, and documents with ease." },
  { name: "PowerSave AI", category: "AI Utility", desc: "Intelligent battery management that learns your habits." },
  { name: "DuplicateFixer", category: "Cleaner", desc: "Find and remove duplicate files across all storage." },
  { name: "PhotoShrink", category: "Optimizer", desc: "Compress galleries by up to 80% without quality loss." },
  { name: "VPN Lite", category: "Security", desc: "Lightning-fast secure connection with zero-log policy." },
  { name: "AdBlock Pro", category: "Utility", desc: "System-wide ad blocking for browsers and in-app ads." },
  { name: "QR Scanner+", category: "Utility", desc: "Instant scan with history, business card creation, and WiFi connect." },
  { name: "PDF Master", category: "Productivity", desc: "Edit, merge, compress, and sign PDFs on the go." },
  { name: "ScreenCast", category: "Productivity", desc: "HD screen recording with built-in editor and sharing." },
];

export default function ProductsPage() {
  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
      <PageNavbar />

      <section className="relative w-full overflow-hidden" style={{ padding: "80px 16px 50px" }}>
        <div className="mx-auto" style={{ maxWidth: 1440 }}>
          <div style={{ fontSize: 12, color: "#8A8A8A", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 16 }}>
            Portfolio
          </div>
          <h1 style={{ fontSize: "clamp(28px, 5vw, 72px)", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05, color: "#ECECEC", margin: 0, maxWidth: 900 }}>
            Fifteen products. Millions of users. One relentless focus on utility.
          </h1>
          <p style={{ fontSize: "clamp(15px, 1.2vw, 20px)", color: "#8A8A8A", lineHeight: 1.6, marginTop: 20, maxWidth: 720 }}>
            Each app in our catalog was built to solve a specific, everyday problem — and every single one has been through the same rigorous cycle: research, develop, test, launch, measure, and optimize until the retention curves climb where they belong.
          </p>
        </div>
      </section>

      {/* How we build */}
      <div className="mx-auto flex flex-col lg:grid gap-10 lg:gap-16" style={{ maxWidth: 1440, padding: "0 16px", marginBottom: 60, gridTemplateColumns: "1fr 1fr" }}>
        <div>
          <h2 style={{ fontSize: "clamp(24px, 2vw, 32px)", fontWeight: 600, color: "#ECECEC", letterSpacing: "-0.02em", marginBottom: 20 }}>
            What makes our products different
          </h2>
          <p style={{ fontSize: 16, color: "#8A8A8A", lineHeight: 1.7, marginBottom: 16 }}>
            We don't chase trends — we chase real user frustration. Every product starts with a pain point we've validated through keyword data, competitor reviews, and user interviews. If people aren't actively searching for a solution, we don't build it.
          </p>
          <p style={{ fontSize: 16, color: "#8A8A8A", lineHeight: 1.7 }}>
            Once an idea clears that bar, it goes through rapid prototyping, cross-platform engineering, rigorous QA, and a launch sequence that includes ASO groundwork, creative production, and paid campaigns — all synchronized to create momentum from day one.
          </p>
        </div>
        <div>
          <h2 style={{ fontSize: "clamp(24px, 2vw, 32px)", fontWeight: 600, color: "#ECECEC", letterSpacing: "-0.02em", marginBottom: 20 }}>
            Categories we own
          </h2>
          <p style={{ fontSize: 16, color: "#8A8A8A", lineHeight: 1.7, marginBottom: 16 }}>
            Our portfolio cuts across six categories — Cleaners, Optimizers, Security, AI Utilities, Productivity, and Recovery. This breadth isn't random: each category feeds insights into the others, and our marketing teams use cross-category data to sharpen targeting.
          </p>
          <p style={{ fontSize: 16, color: "#8A8A8A", lineHeight: 1.7 }}>
            The result? Products that rank, convert, and retain — across different app stores, different geographies, and different user demographics. Browse the full lineup below.
          </p>
        </div>
      </div>

      {/* Products grid */}
      <div className="mx-auto" style={{ maxWidth: 1440, padding: "0 16px", marginBottom: 80 }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {PRODUCTS.map((p, i) => (
            <div
              key={p.name}
              className="hover:border-[rgba(147,197,253,0.3)] hover:-translate-y-1"
              style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 28, background: "rgba(255,255,255,0.02)", transition: "border-color 0.3s ease, transform 0.3s ease" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(147,197,253,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#93C5FD" }}>{i + 1}</span>
                </div>
                <span style={{ fontSize: 11, color: "#8A8A8A", textTransform: "uppercase", letterSpacing: 0.5 }}>{p.category}</span>
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: "#ECECEC", marginBottom: 8 }}>{p.name}</h3>
              <p style={{ fontSize: 14, color: "#8A8A8A", lineHeight: 1.5 }}>{p.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <PageFooter />
    </div>
  );
}